<script setup lang="ts">
import { ref } from 'vue';
import { TranslationService } from '@/services/translation.services';
import type { ICampaignTranslationInput } from './campaign.types';

const langModel = defineModel<'fr' | 'en' | 'nl'>('lang', { required: true });
const translationsModel = defineModel<ICampaignTranslationInput[]>(
  'translations',
  { required: true },
);

const LANGS = [
  { code: 'fr' as const, label: 'Français' },
  { code: 'en' as const, label: 'English' },
  { code: 'nl' as const, label: 'Nederlands' },
];

function fieldFor(code: 'fr' | 'en' | 'nl') {
  return translationsModel.value.find((tr) => tr.languageCode === code);
}

function updateField(
  code: 'fr' | 'en' | 'nl',
  key: 'name' | 'description',
  value: string,
) {
  const existing = fieldFor(code);
  if (existing) {
    existing[key] = value;
  } else {
    translationsModel.value.push({
      languageCode: code,
      name: key === 'name' ? value : '',
      description: key === 'description' ? value : '',
    });
  }
}

const translatingNameSource = ref<'fr' | 'en' | 'nl' | null>(null);
const translatingDescSource = ref<'fr' | 'en' | 'nl' | null>(null);

async function onFieldBlur(
  code: 'fr' | 'en' | 'nl',
  key: 'name' | 'description',
) {
  const value = fieldFor(code)?.[key] ?? '';
  if (!value.trim()) return;
  const busy = key === 'name' ? translatingNameSource : translatingDescSource;
  if (busy.value) return;
  busy.value = code;
  try {
    const translated = await TranslationService.translateText(value, code);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== code)
      .forEach((l) => updateField(l, key, translated[l]));
  } finally {
    busy.value = null;
  }
}
</script>

<template>
  <div class="ctf">
    <div class="ctf__lang-row">
      <label class="ctf__label">Langue de saisie</label>
      <div class="ctf__select-wrap">
        <select v-model="langModel" class="ctf__select">
          <option v-for="l in LANGS" :key="l.code" :value="l.code">
            {{ l.label }}
          </option>
        </select>
      </div>
    </div>

    <div class="ctf__field-row">
      <div v-for="l in LANGS" :key="`name-${l.code}`" class="ctf__field">
        <label class="ctf__label">
          <span
            class="ctf__badge"
            :class="{ 'ctf__badge--active': langModel === l.code }"
            >{{ l.code.toUpperCase() }}</span
          >
          Nom
          <span
            v-if="translatingNameSource && translatingNameSource !== l.code"
            class="ctf__translating"
          >
            Traduction…
          </span>
        </label>
        <input
          class="ctf__input"
          :class="{ 'ctf__input--active': langModel === l.code }"
          type="text"
          :value="fieldFor(l.code)?.name ?? ''"
          :placeholder="`Nom de la campagne (${l.label})`"
          @input="
            updateField(
              l.code,
              'name',
              ($event.target as HTMLInputElement).value,
            )
          "
          @blur="onFieldBlur(l.code, 'name')"
        />
      </div>
    </div>

    <div class="ctf__field-row">
      <div v-for="l in LANGS" :key="`desc-${l.code}`" class="ctf__field">
        <label class="ctf__label">
          <span
            class="ctf__badge"
            :class="{ 'ctf__badge--active': langModel === l.code }"
            >{{ l.code.toUpperCase() }}</span
          >
          Description
          <span
            v-if="translatingDescSource && translatingDescSource !== l.code"
            class="ctf__translating"
          >
            Traduction…
          </span>
        </label>
        <textarea
          class="ctf__textarea"
          :class="{ 'ctf__input--active': langModel === l.code }"
          rows="3"
          :value="fieldFor(l.code)?.description ?? ''"
          :placeholder="`Description (${l.label})`"
          @input="
            updateField(
              l.code,
              'description',
              ($event.target as HTMLTextAreaElement).value,
            )
          "
          @blur="onFieldBlur(l.code, 'description')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.ctf {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.ctf__lang-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.ctf__label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ctf__translating {
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: var(--nxt-primary);
}
.ctf__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.125rem;
  padding: 0 0.25rem;
  border-radius: 0.3rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  font-size: 0.625rem;
  font-weight: 800;
  color: var(--nxt-on-surface-variant);
}
.ctf__badge--active {
  background: var(--nxt-primary);
  color: #fff;
  border-color: var(--nxt-primary);
}
[data-theme='dark'] .ctf__badge--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
.ctf__select-wrap {
  max-width: 12rem;
}
.ctf__select {
  height: 2.25rem;
  width: 100%;
  padding: 0 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
}
.ctf__field-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.ctf__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 12rem;
}
.ctf__input,
.ctf__textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.ctf__input:focus,
.ctf__textarea:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.ctf__input--active {
  border-color: var(--nxt-primary);
}
</style>
